import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

/**
 * Open-state for the quick-nav command palette (`NavCommandMenu`).
 *
 * The palette used to own `open` locally, which made it unreachable from
 * anywhere else: it mounts OUTSIDE `SidebarProvider`, so the sidebar's Page
 * Finder button had no way to open it. Lifting just the open flag here keeps
 * the palette's contents, keybinding, and behaviour untouched while giving any
 * surface an opener.
 */
/** Kory's open_search: a page by its menu name, else the palette prefilled with these words. */
export type CommandMenuRequest = { query: string; page: string };

type CommandMenuContextType = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  openCommandMenu: () => void;
  /** The latest request, for the palette to carry out once; it clears it with `null`. */
  request: CommandMenuRequest | null;
  setRequest: Dispatch<SetStateAction<CommandMenuRequest | null>>;
};

const CommandMenuContext = createContext<CommandMenuContextType | undefined>(undefined);

export function CommandMenuProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CommandMenuRequest | null>(null);
  const openCommandMenu = useCallback(() => setOpen(true), []);
  const value = useMemo(
    () => ({ open, setOpen, openCommandMenu, request, setRequest }),
    [open, openCommandMenu, request],
  );
  return <CommandMenuContext.Provider value={value}>{children}</CommandMenuContext.Provider>;
}

export function useCommandMenu(): CommandMenuContextType {
  const context = useContext(CommandMenuContext);
  if (!context) {
    throw new Error("useCommandMenu must be used within a CommandMenuProvider");
  }
  return context;
}

/** The palette's opener, or null outside the app shell (where there is no palette). */
export function useCommandMenuOpener(): (() => void) | null {
  return useContext(CommandMenuContext)?.openCommandMenu ?? null;
}

/** Kory's open_search, or null outside the app shell (where there is no palette). */
export function useCommandMenuRequest(): ((request: CommandMenuRequest) => void) | null {
  return useContext(CommandMenuContext)?.setRequest ?? null;
}
